import { Button } from "antd";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BrandLogo } from "@/components/BrandLogo";
import { IconMenu } from "@/components/icons/nav";
import { useAttentionBadge } from "@/hooks/useAttentionQueue";
import { useProfile } from "@/hooks/useProfile";
import { useSubscription } from "@/hooks/useSubscription";

interface TopBarProps {
  onMenuClick?: () => void;
}

interface BannerContent {
  message: string;
  ctaLabel: string;
  ctaPath: string;
  iconColor: string;
}

function bannerContentFor(
  banner: NonNullable<ReturnType<typeof useSubscription>["subscription"]>["banner"],
  daysRemaining: number,
  t: TFunction,
): BannerContent | null {
  if (banner === "free_expiring") {
    const message =
      daysRemaining <= 0
        ? t("common:subscription.freeEndsToday")
        : t("common:subscription.freeEndsIn", {
            duration: t("common:subscription.daysLeft", { count: daysRemaining }),
          });
    return {
      message,
      ctaLabel: t("common:subscription.buyPlan"),
      ctaPath: "/billing",
      iconColor: "var(--primary)",
    };
  }
  if (banner === "expired") {
    return {
      message: t("common:subscription.planExpired"),
      ctaLabel: t("common:subscription.buyPlan"),
      ctaPath: "/billing",
      iconColor: "var(--error)",
    };
  }
  if (banner === "pending_payment") {
    return {
      message: t("common:subscription.pendingOrder"),
      ctaLabel: t("common:subscription.viewOrders"),
      ctaPath: "/billing/orders",
      iconColor: "var(--primary)",
    };
  }
  return null;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscription } = useSubscription();
  const { canManageBilling } = useProfile();
  // On phones the sidebar lives in a closed drawer, so the menu button carries
  // the inbox badge's signal until it opens.
  const attentionBadge = useAttentionBadge();

  const rawBanner = subscription ? bannerContentFor(subscription.banner, subscription.daysRemaining, t) : null;
  // pending_payment is only actionable by billing managers — hide entirely for others
  const banner = rawBanner && subscription?.banner === "pending_payment" && !canManageBilling ? null : rawBanner;

  // No banner AND no menu trigger means the bar would be a 48px empty strip
  // (only happens on desktop with an active subscription / no pending payment).
  // Render nothing so the main content slides up.
  if (!banner && !onMenuClick) return null;

  return (
    <div className="flex min-h-12 shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-white px-4 py-2">
      {onMenuClick && (
        <>
          <button
            type="button"
            aria-label={
              attentionBadge ? `${t("common:nav.openMenu")}. ${attentionBadge.label}` : t("common:nav.openMenu")
            }
            onClick={onMenuClick}
            className="relative flex items-center justify-center h-9 w-9 shrink-0 rounded-md text-text-secondary hover:bg-surface-secondary lg:hidden"
          >
            <IconMenu />
            {attentionBadge ? <span className="menu-attention-dot" aria-hidden /> : null}
          </button>
          <div className="flex shrink-0 items-center gap-2 lg:hidden">
            <BrandLogo />
            <span className="whitespace-nowrap text-sm font-bold text-text">Sight Order</span>
          </div>
        </>
      )}

      {banner ? (
        <div className="flex w-full min-w-0 items-center gap-1.5 sm:justify-center sm:gap-2 lg:w-auto lg:flex-1">
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            className="shrink-0 sm:h-4 sm:w-4"
            aria-hidden="true"
          >
            <path d="M8 1.5L14.5 13.5H1.5L8 1.5Z" stroke={banner.iconColor} strokeWidth="1.3" strokeLinejoin="round" />
            <path d="M8 6V9" stroke={banner.iconColor} strokeWidth="1.3" strokeLinecap="round" />
            <circle cx="8" cy="11" r="0.7" fill={banner.iconColor} />
          </svg>

          <span className="min-w-0 text-xs text-text-secondary sm:text-sm">{banner.message}</span>

          {canManageBilling && (
            <Button size="small" type="primary" onClick={() => navigate(banner.ctaPath)}>
              {banner.ctaLabel}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex-1" />
      )}
    </div>
  );
}
